import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { DATE_RANGE_OPTIONS } from '../utils/Invoices.dateRange.utils';
import DatePickerComponent from '../../../common/DatePickerComponent';
import { getUserRoleName } from '../../../../config/utils';

// Subcomponent: Invoice Header
export const InvoiceHeader = ({
    title = 'Invoice Lookup',
    subtitle = 'View and manage all invoices across companies and sellers',
}) => {
    return (
        <div data-testid="invoice-header" className="main__tile_heading cus-btn-include-sec">
            <div>
                <span>{title}</span>
                <p style={{ margin: '5px 0 10px 0', fontSize: '14px', color: '#666' }}>
                    {subtitle}
                </p>
            </div>
        </div>
    );
};

InvoiceHeader.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
};

// Subcomponent: Date Range Quick Filters with Filter Toggle
export const InvoiceDateFiltersWithToggle = ({
    selectedDateRange,
    onDateRangeChange,
    onFilterToggle,
    isFilterOpen,
}) => {
    return (
        <div
            data-testid="date-filter-toggle"
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                margin: '20px 0',
                flexWrap: 'wrap',
            }}
        >
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {DATE_RANGE_OPTIONS.map(range => (
                    <button
                        key={range}
                        data-testid={`range-filter-btn-${range}`}
                        className={`btn cus-seconday-bg-btn top-btn ${selectedDateRange === range ? 'active' : ''}`}
                        onClick={() => onDateRangeChange(range)}
                        style={{
                            backgroundColor: selectedDateRange === range ? '#0066cc' : 'transparent',
                            color: selectedDateRange === range ? 'white' : '#0066cc',
                            border: '1px solid #0066cc',
                        }}
                    >
                        {range}
                    </button>
                ))}
            </div>
            <button
                data-testid="filter-toggle-btn"
                className="btn cus-seconday-bg-btn top-btn"
                onClick={() => onFilterToggle('showFilter', !isFilterOpen)}
            >
                Filter
            </button>
        </div>
    );
};

InvoiceDateFiltersWithToggle.propTypes = {
    selectedDateRange: PropTypes.string.isRequired,
    onDateRangeChange: PropTypes.func.isRequired,
    onFilterToggle: PropTypes.func.isRequired,
    isFilterOpen: PropTypes.bool.isRequired,
};

// Sub-subcomponent: Date Range Filter Section
export const FilterDateRange = ({ filterData, handleDateFieldChange }) => {
    return (
        <>
            <div data-testid="date-range-filter" className="col-lg-3 col-sm-12 mobMb-10">
                <div className="row">
                    <div className="col-12">
                        <div className="labelHead">From:</div>
                    </div>
                    <div className="col-12 fontFix">
                        <DatePickerComponent
                            dateField={filterData.dateFrom}
                            handleDateChange={date => handleDateFieldChange(date, 'dateFrom')}
                            fieldName="dateFrom"
                            dateFormat="MM-dd-yyyy"
                        />
                    </div>
                </div>
            </div>
            <div className="col-lg-3 col-sm-12 mobMb-10">
                <div className="row">
                    <div className="col-12">
                        <div className="labelHead">To:</div>
                    </div>
                    <div className="col-12 fontFix">
                        <DatePickerComponent
                            dateField={filterData.dateTo}
                            handleDateChange={date => handleDateFieldChange(date, 'dateTo')}
                            fieldName="dateTo"
                            dateFormat="MM-dd-yyyy"
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

FilterDateRange.propTypes = {
    filterData: PropTypes.shape({
        dateFrom: PropTypes.string,
        dateTo: PropTypes.string,
    }).isRequired,
    handleDateFieldChange: PropTypes.func.isRequired,
};

// Sub-subcomponent: Company Filter
export const FilterCompany = ({ filterData, companyList, handleMultiSelect, userRole, roleList }) => {
    const isSuperAdmin = getUserRoleName(roleList, 'Super Admin', userRole);
    
    return (
        <div data-testid="company-filter" className="col-lg-3 col-sm-12 mobMb-10">
            <div className="row">
                <div className="col-12">
                    <div className="labelHead">Company</div>
                </div>
                <div className="col-12 fontFix">
                    <Select
                        isMulti={!isSuperAdmin}
                        closeMenuOnSelect={false}
                        options={companyList}
                        classNamePrefix="list"
                        name="companyIdList"
                        value={filterData?.companyIdList}
                        onChange={value => handleMultiSelect(value, 'companyIdList')}
                    />
                </div>
            </div>
        </div>
    );
};

FilterCompany.propTypes = {
    filterData: PropTypes.shape({
        companyIdList: PropTypes.array,
    }).isRequired,
    companyList: PropTypes.array.isRequired,
    handleMultiSelect: PropTypes.func.isRequired,
    userRole: PropTypes.object,
    roleList: PropTypes.array,
};

FilterCompany.defaultProps = {
    userRole: null,
    roleList: [],
};

// Sub-subcomponent: Seller Filter
export const FilterSeller = ({ filterData, sellerList, handleMultiSelect, userRole, roleList }) => {
    const isSuperAdmin = getUserRoleName(roleList, 'Super Admin', userRole);
    
    return (
        <div data-testid="seller-filter" className="col-lg-3 col-sm-12 mobMb-10">
            <div className="row">
                <div className="col-12">
                    <div className="labelHead">Seller</div>
                </div>
                <div className="col-12 fontFix">
                    <Select
                        isMulti={!isSuperAdmin}
                        closeMenuOnSelect={false}
                        options={sellerList}
                        classNamePrefix="list"
                        name="sellerIdList"
                        value={filterData?.sellerIdList}
                        onChange={value => handleMultiSelect(value, 'sellerIdList')}
                    />
                </div>
            </div>
        </div>
    );
};

FilterSeller.propTypes = {
    filterData: PropTypes.shape({
        sellerIdList: PropTypes.array,
    }).isRequired,
    sellerList: PropTypes.array.isRequired,
    handleMultiSelect: PropTypes.func.isRequired,
    userRole: PropTypes.object,
    roleList: PropTypes.array,
};

FilterSeller.defaultProps = {
    userRole: null,
    roleList: [],
};

// Sub-subcomponent: Filter Action Buttons
export const FilterActions = ({ onReset, onFilter, filterLabel = 'Apply' }) => {
    return (
        <div data-testid="filter-actions" className="alignRightRes mb-3 pr-0">
            <button
                className="btn cus-primary-transparent-btn top-btn clearFilterBtn"
                onClick={e => onReset(e, 'clear')}
            >
                Clear Filter
            </button>
            <button className="cus-seconday-bg-btn btn filterBtn" onClick={() => onFilter(null)}>
                {filterLabel}
            </button>
        </div>
    );
};

FilterActions.propTypes = {
    onReset: PropTypes.func.isRequired,
    onFilter: PropTypes.func.isRequired,
    filterLabel: PropTypes.string,
};

// Subcomponent: Advanced Filter Section
export const InvoiceAdvancedFilter = ({
    filterData,
    setfilterData,
    onFilter,
    onReset,
    companyList,
    sellerList,
    handleMultiSelect,
    handleDateFieldChange,
    userRole,
    roleList,
    isAdmin = true,
}) => {
    return (
        <div
            data-testid="advanced-filter-section"
            className="filterWrapperGradient filterContainer filterContainerZIndex show historyFilter zIndex2 pt-2"
        >
            <div className="decorativeElem"></div>
            <div className="row mb-2">
                <FilterDateRange filterData={filterData} handleDateFieldChange={handleDateFieldChange} />
                {isAdmin && (
                    <FilterCompany
                        filterData={filterData}
                        companyList={companyList}
                        handleMultiSelect={handleMultiSelect}
                        userRole={userRole}
                        roleList={roleList}
                    />
                )}
                <FilterSeller 
                    filterData={filterData} 
                    sellerList={sellerList} 
                    handleMultiSelect={handleMultiSelect}
                    userRole={userRole}
                    roleList={roleList}
                />
            </div>
            <FilterActions onReset={onReset} onFilter={onFilter} />
        </div>
    );
};

InvoiceAdvancedFilter.propTypes = {
    filterData: PropTypes.object.isRequired,
    setfilterData: PropTypes.func.isRequired,
    onFilter: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    companyList: PropTypes.array.isRequired,
    sellerList: PropTypes.array.isRequired,
    handleMultiSelect: PropTypes.func.isRequired,
    handleDateFieldChange: PropTypes.func.isRequired,
    userRole: PropTypes.object,
    roleList: PropTypes.array,
    isAdmin: PropTypes.bool,
};

InvoiceAdvancedFilter.defaultProps = {
    userRole: null,
    roleList: [],
    isAdmin: true,
};
